class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.maxRecentSearches = 5;

    if (this.input) this.setupEventListeners();
  }

  setupEventListeners() {
    const predictiveSearchMask = document.querySelector('.predictive-search-mask ');
    const cartLink = document.querySelector('.header__cart-link');
    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('blur', (event) => {
      const eventTarget = event.target;

      if (!eventTarget.closest('.header')) {
        this.onFocusOut();
      }
    });
    predictiveSearchMask?.addEventListener('click', this.onFocusOut.bind(this));
    cartLink?.addEventListener('click', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  saveRecentSearch(query) {
    let recentSearches = JSON.parse(localStorage.getItem('recentSearches')) || [];

    if (!recentSearches.includes(query)) {
      recentSearches.unshift(query);

      if (recentSearches.length > this.maxRecentSearches) {
        recentSearches.pop();
      }

      localStorage.setItem('recentSearches', JSON.stringify(recentSearches));
    }
  }

  renderRecentSearches() {
    const recentSearches = JSON.parse(localStorage.getItem('recentSearches')) || [];
    const recentList = document.getElementById('predictive-search-results-recent-list');
    const recentWrapper = document.getElementById('recent-searches-wrapper');

    if (recentList && recentWrapper) {
      if (recentSearches.length > 0) {
        recentWrapper.style.display = 'flex';
        recentList.innerHTML = recentSearches
          .map(
            (search) => `
            <li class="predictive-search__recent-item">
              <a href="/search?q=${encodeURIComponent(search)}" class="predictive-search__item search-link text-green text-[17px] leading-[25px] capitalize [&_mark]:text-gray-800 [&_mark]:hover:text-white hover:underline">
                <div class="predictive-search__item-content">
                  <span class="predictive-search__item-heading predictive-search__item-query-result relative z-[3] whitespace-nowrap">
                    ${search}
                  </span>
                </div>
              </a>
            </li>`
          )
          .join('');

        recentList.querySelectorAll('[data-search-query]').forEach((link) => {
          link.addEventListener('click', (event) => {
            this.input.value = event.target.getAttribute('data-search-query');
            this.onChange();
          });
        });
      } else {
        recentWrapper.style.display = 'none';
      }
    }
  }

  onFormSubmit(event) {
    const searchQuery = this.getQuery();
    if (!searchQuery.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();

    if (searchQuery) {
      this.saveRecentSearch(searchQuery);
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const firstMatchIndex = currentButtonText.indexOf(previousTerm);
      const hasMultipleMatches =
        firstMatchIndex !== -1 && currentButtonText.indexOf(previousTerm, firstMatchIndex + previousTerm.length) !== -1;
      if (hasMultipleMatches) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!resultsSection) {
          this.close();
          return;
        }

        const resultsMarkup = resultsSection.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.renderRecentSearches();
    this.open();
    this.bind();
  }

  bind() {
    const links = document.querySelectorAll('.search-link');

    links.forEach(element => {
      element.addEventListener('click', (event) => {
        const searchQuery = this.getQuery();
        if (!searchQuery.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();

        if (searchQuery) {
          this.saveRecentSearch(searchQuery);
        }
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const resultCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (resultCount) this.setLiveRegionText(resultCount.textContent);
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.input || !this.predictiveSearchResults) return;

    const root = document.querySelector('body');

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    if (this.input.classList.contains('header-search')) {
      root.classList.add('search-active');
      root.classList.add('no-scroll');
    }
  }

  close(clearSearchTerm = false) {
    const root = document.querySelector('body');

    this.closeResults(clearSearchTerm);
    root.classList.remove('search-active');
    this.isOpen = false;
    root.classList.remove('no-scroll');
  }

  closeResults(clearSearchTerm = false) {
    const root = document.querySelector('body');

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false)

    root.classList.remove('search-active');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    root.classList.remove('no-scroll');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
